import type { ThreadMessageLike } from '@assistant-ui/react'
import type { TimelinePreferences } from '@web/state/timeline-preferences'

export type ThoughtTrace = {
  id: string
  text: string
  startedAt?: number
  endedAt?: number
}
export type ThoughtPresentation = { traces: ThoughtTrace[]; latest: boolean }
const custom = (message: ThreadMessageLike) => message.metadata?.custom ?? {}
const stamp = (message: ThreadMessageLike): number | undefined => {
  const timestamp =
    custom(message).timestamp ?? custom(message).lifecycleTimestamp
  return typeof timestamp === 'number' && timestamp > 0 ? timestamp : undefined
}
const hasContent = (message: ThreadMessageLike) =>
  typeof message.content === 'string'
    ? !!message.content.trim()
    : message.content.some((part) =>
        part.type === 'text' || part.type === 'reasoning'
          ? !!part.text.trim()
          : true,
      )

/** Present published traces without changing canonical messages or their replay order. */
export function timelineMessages(
  messages: ThreadMessageLike[],
  preferences: TimelinePreferences,
): ThreadMessageLike[] {
  const visible = messages.filter(
    (message) => hasContent(message) || message.status?.type === 'incomplete',
  )
  const lastThought = [...visible]
    .reverse()
    .find((message) => custom(message).kind === 'reasoning')?.id
  const projected: ThreadMessageLike[] = []
  for (let index = 0; index < visible.length; index++) {
    const message = visible[index]!
    const meta = custom(message)
    if (meta.kind !== 'reasoning') {
      projected.push(message)
      continue
    }
    const start = stamp(message)
    const trace: ThoughtTrace = {
      id: message.id!,
      text:
        typeof message.content === 'string'
          ? message.content
          : message.content
              .map((part) => (part.type === 'reasoning' ? part.text : ''))
              .join(''),
      startedAt: start,
      endedAt:
        message.status?.type === 'running'
          ? undefined
          : typeof meta.finishedTimestamp === 'number'
            ? meta.finishedTimestamp
            : typeof meta.updatedTimestamp === 'number'
              ? meta.updatedTimestamp
              : start,
    }
    const previous = projected.at(-1)
    const previousMeta = previous ? custom(previous) : undefined
    const sameConversation =
      (previousMeta?.conversation as { sessionId?: string } | undefined)
        ?.sessionId ===
      (meta.conversation as { sessionId?: string } | undefined)?.sessionId
    if (
      preferences.thinkingMode === 'compact' &&
      previous &&
      previousMeta?.kind === 'reasoning' &&
      previousMeta.requestId === meta.requestId &&
      sameConversation &&
      (start === undefined ||
        start -
          Number(
            previousMeta.updatedTimestamp ?? previousMeta.timestamp ?? start,
          ) <
          preferences.gapMinutes * 60)
    ) {
      const thought = previousMeta.thought as ThoughtPresentation
      projected[projected.length - 1] = {
        ...previous,
        status: message.status,
        content: [...thought.traces, trace].map((entry) => ({
          type: 'reasoning',
          text: entry.text,
        })),
        metadata: {
          custom: {
            ...previousMeta,
            updatedTimestamp: meta.updatedTimestamp,
            thought: {
              traces: [...thought.traces, trace],
              latest: message.id === lastThought,
            },
          },
        },
      }
    } else {
      projected.push({
        ...message,
        metadata: {
          ...message.metadata,
          custom: {
            ...meta,
            thought: { traces: [trace], latest: message.id === lastThought },
          },
        },
      })
    }
  }
  const result: ThreadMessageLike[] = []
  let previousTime: number | undefined
  for (const message of projected) {
    const time = stamp(message)
    if (
      time !== undefined &&
      (previousTime === undefined ||
        time - previousTime >= preferences.gapMinutes * 60)
    ) {
      result.push({
        id: `time:${message.id}`,
        role: 'system',
        content: [{ type: 'text', text: 'Timeline timestamp' }],
        metadata: {
          custom: {
            kind: 'time-separator',
            timestamp: time,
            conversation: custom(message).conversation,
          },
        },
      })
    }
    if (time !== undefined)
      previousTime = Math.max(
        previousTime ?? 0,
        time,
        Number(custom(message).updatedTimestamp) || time,
      )
    result.push(message)
  }
  return result
}

export function timelineTimeLabel(timestamp: number, now = new Date()): string {
  const date = new Date(timestamp * 1000)
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const day =
    date.toDateString() === now.toDateString()
      ? 'Today'
      : date.toDateString() === yesterday.toDateString()
        ? 'Yesterday'
        : date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            ...(date.getFullYear() !== now.getFullYear()
              ? { year: 'numeric' }
              : {}),
          })
  return `${day} ${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
}
